import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, noop } from 'lodash';
import TrashIcon from 'zignal-landscape/lib/Icon/TrashIcon';
import Save1Icon from 'zignal-landscape/lib/Icon/Save1Icon';
import ConfirmationModal from 'zignal-landscape/lib/ConfirmationModal';
import Styles from '../AuthorGroups.css';
import { AuthorGroupsContext } from '../AuthorGroups';
import AuthorGroupMember from './AuthorGroupMember';

const AuthorGroup = ({
  isReadOnly,
  authorsLimit = 0,
  domainsLimit = 0,
  mediaOutletsLimit = 0,
  handleSaveAuthorGroup = noop,
  handleDeleteAuthorGroup = noop
}) => {
  const { state, dispatch } = useContext(AuthorGroupsContext);
  const [ isSaving, setIsSaving ] = useState(false);
  const [ isDelConfirmModalOpen, setIsDelConfirmModalOpen ] = useState(false);

  const handleNameChange = (e) => {
    e.preventDefault();
    dispatch({type: 'set-author-group-name', name: e.target.value});
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur(); // lose focus
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    handleSaveAuthorGroup(setIsSaving);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    setIsDelConfirmModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDelConfirmModalOpen(false);
  };

  const handleConfirmDelete = () => {
    setIsDelConfirmModalOpen(false);
    handleDeleteAuthorGroup();
  };

  const isSaveDisabled = () => {
    if (isReadOnly || isSaving ||
        isEmpty(state.selectedAuthorGroup.name) ||
        isEmpty(state.selectedAuthorGroup.members)) {

      return true;
    }
    let memberHasNoAuthors = false;
    Object.values(state.selectedAuthorGroup.members).forEach(member => {
      if (isEmpty(member.authors)) {
        memberHasNoAuthors = true;
      }
    });
    return memberHasNoAuthors;
  };

  return (
    <div className={Styles.authorGroupPanel} data-cy="author-group-panel">
      <div className={Styles.authorGroupTitle}>{state.authorGroupTitle}</div>
      <div className={Styles.authorGroupName}>
        <label className={Styles.authorGroupNameLabel} htmlFor="name">Group Name</label>
        <input
          className={Styles.authorGroupNameInput}
          type="text"
          name="name"
          placeholder={'Add Group name ...'}
          value={state.selectedAuthorGroup.name}
          onChange={handleNameChange}
          onKeyDown={handleNameKeyDown}
          disabled={isReadOnly}
        />
      </div>
      <div className={Styles.authorGroupNumSummary}>
        <span className={Styles.authorsNumSummary}>
          {`Number of Authors: ${state.authorsTotalCounts?.author}/${authorsLimit}`}
        </span>
        <span className={Styles.domainsNumSummary}>
          {`Number of Domains: ${state.authorsTotalCounts?.domain}/${domainsLimit}`}
        </span>
        <span className={Styles.mediaOutletsNumSummary}>
          {`Number of Media Outlets: ${state.authorsTotalCounts?.media_outlet}/${mediaOutletsLimit}`}
        </span>
      </div>
      {
        state.memberKeyList.map(memberKey => (
          <AuthorGroupMember
            isReadOnly={isReadOnly}
            key={memberKey}
            memberKey={memberKey}
            member={{...state.selectedAuthorGroup.members[memberKey]}}
          />
        ))
      }
      <div className={Styles.authorGroupButtons}>
        <button type="button" className={Styles.authorGroupSaveBtn} onClick={handleSave} disabled={isSaveDisabled()}>
          {!isSaving && <Save1Icon />}
          {isSaving ? ' Saving ...' : ' Save Group'}
        </button>
        <button type="button" className={Styles.authorGroupDeleteBtn} onClick={handleDelete} disabled={isReadOnly}>
          <TrashIcon className={Styles.authorGroupDeleteIcon} />
          {' Delete Group'}
        </button>
      </div>
      <ConfirmationModal
        data-cy="author-group-delete-confirm-modal"
        confirmStyle="delete"
        isOpen={isDelConfirmModalOpen}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="DELETE GROUP"
      >
        <strong>Are you sure you want to delete this group?</strong>
      </ConfirmationModal>
    </div>
  );
};

AuthorGroup.propTypes = {
  isReadOnly: PropTypes.bool,
  authorsLimit: PropTypes.number,
  domainsLimit: PropTypes.number,
  mediaOutletsLimit: PropTypes.number,
  handleSaveAuthorGroup: PropTypes.func,
  handleDeleteAuthorGroup: PropTypes.func
};

export default AuthorGroup;
